import React from 'react';
import moment from 'moment/moment';
import { formatCurrency } from 'utils';
import { Info } from '@mui/icons-material';
import { differenceInDays } from 'date-fns';
import { DateFormats } from 'constants/common';
import { capitalizeFirstLetter } from 'formatters';
import { Column } from 'components/shared/TableListing';
import { green, red, yellow } from '@mui/material/colors';
import { Box, Stack, Tooltip, Typography } from '@mui/material';

import { CloseDateCell, DealStatusDot } from './cells';
import { DealStatusLegend } from './DealStatusLegend';
import { BPFDealStatus, ReportsListingViewModel } from '../../types';

const ROW_TINT: Record<BPFDealStatus, string> = {
  [BPFDealStatus.Healthy]: 'white',
  [BPFDealStatus.Hot]: '#FDF2F8',
  [BPFDealStatus.Stalled]: '#FFFBEB',
  [BPFDealStatus.AtRisk]: '#FEF3F2',
};

const getRowBg = (row: ReportsListingViewModel) => ROW_TINT[row.dealStatus] ?? 'white';

export function getReportsListingConfig(): Column<ReportsListingViewModel>[] {
  return [
    {
      width: '6rem',
      header: 'Status',
      renderKey: 'dealStatus',
      bodyStyles: ({ row }) => ({ backgroundColor: getRowBg(row) }),
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" justifyContent="center" width="100%">
          <DealStatusDot status={row.dealStatus} />
        </Box>
      ),
      renderHeadCell: () => (
        <Stack direction="row" gap="0.5rem" alignItems="center">
          <Tooltip arrow placement="bottom-start" title={<DealStatusLegend />}>
            <Info color="primary" fontSize="small" />
          </Tooltip>

          <Typography variant="subtitle2" component="span">
            Status
          </Typography>
        </Stack>
      ),
    },
    {
      width: '10rem',
      header: 'Customer',
      renderKey: 'customer',
      bodyStyles: ({ row }) => ({ backgroundColor: getRowBg(row) }),
      renderCell: ({ row }) => <Typography>{row.customer}</Typography>,
    },
    {
      width: '15rem',
      header: 'Close Date',
      renderKey: 'closeDate',
      bodyStyles: ({ row }) => ({ backgroundColor: getRowBg(row) }),
      renderCell: ({ row }) => <CloseDateCell row={row} />,
    },
    {
      width: '8rem',
      header: 'Location',
      renderKey: 'location',
      bodyStyles: ({ row }) => ({ backgroundColor: getRowBg(row) }),
      renderCell: ({ row }) => <Typography>{row.location || 'N/A'}</Typography>,
    },
    {
      width: '9rem',
      header: 'Invoice Amount',
      renderKey: 'invoiceAmount',
      bodyStyles: ({ row }) => ({ backgroundColor: getRowBg(row) }),
      renderCell: ({ row }) => <Typography>{row.invoiceAmount ? formatCurrency(row.invoiceAmount) : 'N/A'}</Typography>,
    },
    {
      width: '8rem',
      header: 'Gross Profit',
      renderKey: 'grossProfit',
      bodyStyles: ({ row }) => ({ backgroundColor: getRowBg(row) }),
      renderCell: ({ row }) => (
        <Typography>{row.grossProfit !== null ? formatCurrency(row.grossProfit, true) : 'N/A'}</Typography>
      ),
    },
    {
      width: '9rem',
      header: 'Probability %',
      renderKey: 'probability',
      renderHeadCell: () => (
        <Stack direction="row" gap="0.5rem" alignItems="center">
          <Tooltip placement="top" arrow title="Best VO or Stage probability">
            <Info color="primary" fontSize="small" />
          </Tooltip>
          <Typography variant="subtitle2" component="span">
            Probability %
          </Typography>
        </Stack>
      ),
      bodyStyles: ({ row }) => ({ backgroundColor: getRowBg(row) }),
      renderCell: ({ row }) => <Typography>{row.probability ? `${row.probability}%` : 'N/A'}</Typography>,
    },
    {
      width: '11rem',
      header: 'Weighted Amount',
      renderKey: 'weightedAmount',
      bodyStyles: ({ row }) => ({ backgroundColor: getRowBg(row) }),
      renderHeadCell: () => (
        <Stack direction="row" gap="0.5rem" alignItems="center">
          <Tooltip placement="top" arrow title="Weighted Amount = Invoice Total x Probability %">
            <Info color="primary" fontSize="small" />
          </Tooltip>
          <Typography variant="subtitle2" component="span">
            Weighted Amount
          </Typography>
        </Stack>
      ),
      renderCell: ({ row }) => (
        <Typography>{row.weightedAmount !== null ? formatCurrency(row.weightedAmount, true) : 'N/A'}</Typography>
      ),
    },
    {
      header: 'Stage',
      renderKey: 'stage',
      bodyStyles: ({ row }) => ({ backgroundColor: getRowBg(row) }),
      renderCell: ({ row }) => {
        const getColorByDays = (days: number) => {
          if (days <= 5) return green[600];
          if (days <= 10) return yellow[600];
          return red[600];
        };

        return (
          <Stack width="100%" direction="row" justifyContent="space-between">
            <Stack>
              <Typography>{row.stage}</Typography>
              {row.department && (
                <Typography variant="caption" color="textDisabled">
                  {capitalizeFirstLetter(row.department)}
                </Typography>
              )}
            </Stack>
            <Stack>
              {row.closeStatus ? (
                <Typography
                  sx={{
                    borderRadius: '0.4rem',
                    padding: '0.1rem 0.2rem',
                    backgroundColor: row.closeStatus === 'SUCCESS' ? green[50] : red[50],
                  }}
                  color={row.closeStatus === 'SUCCESS' ? 'green' : 'red'}
                >
                  {row.closeStatus === 'SUCCESS' ? 'Won' : 'Lost'}
                </Typography>
              ) : (
                <Typography sx={{ color: getColorByDays(Number(row.daysInStage)) }}>
                  {row.daysInStage !== null ? `${row.daysInStage} Days` : 'N/A'}
                </Typography>
              )}
            </Stack>
          </Stack>
        );
      },
    },
    {
      width: '11rem',
      header: 'Created',
      renderKey: 'createdDateTime',
      bodyStyles: ({ row }) => ({ backgroundColor: getRowBg(row) }),
      renderCell: ({ row }) => (
        <Stack>
          <Typography>{moment(row.createdDateTime).format(DateFormats.DateTimeMedium24Hour)}</Typography>
          <Typography align="center" variant="caption" color="textDisabled">
            {differenceInDays(new Date(), new Date(row.createdDateTime))} days
          </Typography>
        </Stack>
      ),
    },
  ];
}
